import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import API_BASE_URL, { API_ENDPOINTS } from '../config';
import PageTemplate from './PageTemplate';
import { InventoryContent } from './InventoryContent';
import { RotateCcw } from 'lucide-react';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/trash.css';

interface TrashItem {
  id: number;
  name: string;
  description: string;
  category: string;
  color: string;
  area: string;
  location: string;
  created_at: string;
  deleted_at: string;
  quantity?: number;
  stock_keeping?: boolean;
  quantity_min?: number;
  sale_price?: number;
  image_id?: number;
  image_url?: string;
  thumbnail_url?: string;
  image_width?: number;
  image_height?: number;
}

type FilterType = 'all' | 'today' | 'yesterday' | 'last_delete';

interface TrashViewProps {
  onTrashCountChange?: (total: number, filtered: number, selected: number) => void;
  filter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  onStateChange: (state: {
    selectedCount: number;
    totalCount: number;
    restoring: boolean;
    emptying: boolean;
  }) => void;
  onCallbacksReady: (callbacks: {
    onSelectAll?: () => void;
    onClearSelection?: () => void;
    onRestore?: () => void;
    onEmptyTrash?: () => void;
    onEmptySelected?: () => void;
  }) => void;
}

const TrashView: React.FC<TrashViewProps> = ({ 
  onTrashCountChange, 
  filter, 
  onFilterChange,
  onStateChange,
  onCallbacksReady 
}) => {
  const { token } = useAuth();
  const { activeSpace } = useSpace();
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [restoring, setRestoring] = useState(false);
  const [emptying, setEmptying] = useState(false);
  const [menuItem, setMenuItem] = useState<TrashItem | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [menuReady, setMenuReady] = useState(false);

  const fetchTrashItems = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const spaceParam = activeSpace ? `&space_id=${activeSpace.id}` : '';
      const response = await fetch(`${API_ENDPOINTS.GET_TRASH(filter)}${spaceParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch trash items');
      
      const data = await response.json();
      setItems(data.items || []);
      setError(null);
    } catch (err) {
      setError('Failed to load trash items');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filter, activeSpace]);

  useEffect(() => {
    fetchTrashItems();
  }, [fetchTrashItems]);

  // Update counts whenever items or selection changes
  useEffect(() => {
    if (onTrashCountChange) {
      onTrashCountChange(items.length, items.length, selectedIds.size);
    }
  }, [items.length, selectedIds.size, onTrashCountChange]);

  // Notify parent of state changes
  useEffect(() => {
    onStateChange({
      selectedCount: selectedIds.size,
      totalCount: items.length,
      restoring,
      emptying,
    });
  }, [selectedIds.size, items.length, restoring, emptying, onStateChange]);

  const handleRestore = useCallback(async () => {
    if (!token || selectedIds.size === 0) return;
    
    try {
      setRestoring(true);
      const response = await fetch(API_ENDPOINTS.RESTORE_TRASH, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_ids: Array.from(selectedIds) }),
      });
      
      if (!response.ok) throw new Error('Failed to restore items');
      
      const data = await response.json();
      // No alert needed - items just appear back in inventory
      
      setSelectedIds(new Set());
      await fetchTrashItems();
    } catch (err) {
      console.error('Restore error:', err);
      alert('Failed to restore items');
    } finally {
      setRestoring(false);
    }
  }, [token, selectedIds, fetchTrashItems]);

  const handleRestoreSingle = async (itemId: number) => {
    if (!token) return;
    
    try {
      setRestoring(true);
      const response = await fetch(API_ENDPOINTS.RESTORE_TRASH, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_ids: [itemId] }),
      });
      
      if (!response.ok) throw new Error('Failed to restore item');
      
      await fetchTrashItems();
      setMenuPosition(null);
      setMenuItem(null);
    } catch (err) {
      console.error('Restore error:', err);
      alert('Failed to restore item');
    } finally {
      setRestoring(false);
    }
  };

  const handleMenuClick = (item: TrashItem) => {
    setMenuItem(item);
    setMenuReady(false);
    
    // Center the menu on screen
    setTimeout(() => {
      setMenuPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      setMenuReady(true);
    }, 10);
  };

  const handleEmptyTrash = useCallback(async (emptyAll: boolean = false) => {
    if (!token) return;
    
    const message = emptyAll 
      ? 'Permanently delete ALL items in trash? This cannot be undone!'
      : 'Permanently delete items older than your retention period? This cannot be undone!';
    
    if (!window.confirm(message)) return;
    
    try {
      setEmptying(true);
      const spaceParam = activeSpace ? `&space_id=${activeSpace.id}` : '';
      const response = await fetch(`${API_ENDPOINTS.EMPTY_TRASH(emptyAll)}${spaceParam}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to empty trash');
      
      const data = await response.json();
      alert(`Permanently deleted ${data.deleted_count} item(s)`);
      
      await fetchTrashItems();
    } catch (err) {
      console.error('Empty trash error:', err);
      alert('Failed to empty trash');
    } finally {
      setEmptying(false);
    }
  }, [token, fetchTrashItems]);

  const handleEmptySelected = useCallback(async () => {
    if (!token || selectedIds.size === 0) return;
    
    if (!window.confirm(`Permanently delete ${selectedIds.size} selected item(s)? This cannot be undone!`)) return;
    
    try {
      setEmptying(true);
      const spaceParam = activeSpace ? `&space_id=${activeSpace.id}` : '';
      const response = await fetch(`${API_ENDPOINTS.EMPTY_TRASH(false)}${spaceParam}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_ids: Array.from(selectedIds) }),
      });
      
      if (!response.ok) throw new Error('Failed to delete selected items');
      
      const data = await response.json();
      alert(`Permanently deleted ${data.deleted_count} item(s)`);
      
      setSelectedIds(new Set());
      await fetchTrashItems();
    } catch (err) {
      console.error('Delete selected error:', err);
      alert('Failed to delete selected items');
    } finally {
      setEmptying(false);
    }
  }, [token, selectedIds, fetchTrashItems]);

  // Expose callbacks to parent
  useEffect(() => {
    onCallbacksReady({
      onSelectAll: () => setSelectedIds(new Set(items.map(i => i.id))),
      onClearSelection: () => setSelectedIds(new Set()),
      onRestore: handleRestore,
      onEmptyTrash: () => handleEmptyTrash(true),
      onEmptySelected: handleEmptySelected,
    });
  }, [items, handleRestore, handleEmptyTrash, handleEmptySelected, onCallbacksReady]);

  const formatDeletedDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <PageTemplate
      showPageHeader={false}
      showInputArea={false}
      hasGrayBackground={true}
      enableSmartScroll={false}
      stickyHeader={true}
      stickyInput={true}
      className="trash-page"
    >
      {error && (
        <div className="trash-error">
          <p>{error}</p>
          <button onClick={fetchTrashItems}>Retry</button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="trash-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
          <h3>Trash is empty</h3>
          <p>Deleted items will appear here</p>
        </div>
      )}


      <InventoryContent
        items={items}
        viewMode="card"
        token={token}
        selectedIds={selectedIds}
        isSelectMode={true}
        onItemClick={(item: any) => {
          const newSelection = new Set(selectedIds);
          if (newSelection.has(item.id)) {
            newSelection.delete(item.id);
          } else {
            newSelection.add(item.id);
          }
          setSelectedIds(newSelection);
        }}
        onMenuClick={(item: any) => handleMenuClick(item as TrashItem)}
        renderItemOverlay={(item: any) => {
          const trashItem = item as TrashItem;
          return (
            <div className="trash-item-overlay">
              <span className="deleted-time">
                Deleted {formatDeletedDate(trashItem.deleted_at)}
              </span>
            </div>
          );
        }}
      />

      {/* Item menu dropdown - rendered via portal at body level */}
      {menuItem && ReactDOM.createPortal(
        <>
          <div 
            className="menu-backdrop" 
            onClick={() => {
              if (menuReady) {
                setMenuPosition(null);
                setMenuItem(null);
                setMenuReady(false);
              }
            }}
          />
          <div 
            className="trash-item-menu"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000
            }}
          >
            {/* Header */}
            <div className="trash-menu-header">
              <span className="trash-menu-title">Item #{menuItem.id}</span>
              <ModalCloseButton onClick={() => {
                  setMenuPosition(null);
                  setMenuItem(null);
                  setMenuReady(false);
                }} />
            </div>
            
            {/* Menu items */}
            <button
              className="trash-menu-item"
              onClick={() => handleRestoreSingle(menuItem.id)}
              disabled={restoring}
            >
              <RotateCcw size={18} />
              <span>Restore</span>
            </button>
          </div>
        </>,
        document.body
      )}
    </PageTemplate>
  );
};

export default TrashView;
